import { useTranslation } from "react-i18next";
import type { ProjectedHumanStep } from "../../../api/threads/liveMessages";
import Chip from "../../../ui/feedback/Chip";
import SpinnerIcon from "../../../ui/feedback/SpinnerIcon";
import type { ChipTone } from "../../../ui/styles/chipStyles";
import { requestTurnText, requestWaitText } from "./requestWaitWords";
import type { ThreadDisplayStatus } from "./threadStatus";

/**
 * The statuses that say a Run's progress. `live` and `saved` say only its connection, which the Run's connection sign
 * shows (Johannes, 4 Nov 09:28: "i feel like some spillover has happened"), so they get no badge.
 */
type ProgressStatus = Exclude<ThreadDisplayStatus, "live" | "saved">;

const isProgress = (status: ThreadDisplayStatus): status is ProgressStatus => status !== "live" && status !== "saved";

/** His colours (4 Nov 09:28): needs input blue, failed red; working and waiting stay blue, closed grey. */
const STATUS_TONE: Readonly<Record<ProgressStatus, ChipTone>> = {
  working: "info",
  waiting: "info",
  approval: "warning",
  input: "info",
  failed: "danger",
  closed: "neutral",
};

interface ThreadStatusBadgeProps {
  status: ThreadDisplayStatus;
  /** The person step that waits, from the same projection, and the host's name (`null` when the viewer hosts): Input then says whom it waits for, and names the host where the turn is theirs. */
  request?: { readonly step: ProjectedHumanStep; readonly hostName: string | null } | null;
  compact?: boolean;
  className?: string;
}

/** One status per Thread, the same in the list and on the page; none while its only status is its connection. */
export default function ThreadStatusBadge({ status, request = null, compact = false, className }: ThreadStatusBadgeProps) {
  const { t } = useTranslation();
  if (!isProgress(status)) return null;
  const turn = status === "input" && request !== null ? requestTurnText(request.step, request.hostName, t) : null;
  return (
    <Chip
      tone={STATUS_TONE[status]}
      size={compact ? "compact" : "small"}
      title={status === "input" && request !== null ? requestWaitText(request.step, null, request.hostName, t) : t(`threads.status.${status}.explanation`)}
      icon={status === "working" ? <SpinnerIcon className="h-3 w-3" /> : null}
      className={className === undefined ? "shrink-0" : `shrink-0 ${className}`}
    >
      {turn ?? t(`threads.status.${status}.label`)}
    </Chip>
  );
}
